import createDebug from 'debug';
import { getRetryMillisecondsWithExponentialBackoff } from '@/functions/getRetryMillisecondsWithExponentialBackoff';
import { sleep } from '@/functions/sleep';

// See the Debug Logging section of the ML's README.md for how to use this.
const debug = createDebug('durable:RetryPromiseBase');

export class RetryPromiseBase {
  constructor(errorLoggingFn, httpCodesToFailImmediately) {
    this.errorLoggingFn = errorLoggingFn;
    this.maxAttempts = 100;
    this.httpCodesToFailImmediately = httpCodesToFailImmediately;
  }

  /**
   * Runs a promise and retries it if it fails, with time between attempts exponentially backing
   * off. Returns the result of the promise if it succeeds, or throws an error if it fails after
   * the maximum number of attempts.
   *
   * @template {unknown} T
   * @param {() => Promise<T>} promiseFunctionToRetry A function that returns a promise and that
   *   promise returns a value that will be returned by this function
   * @returns {Promise<T>} The result of the promise returned by promiseFunctionToRetry
   */
  async run(promiseFunctionToRetry) {
    let attempts = 1;

    const methodDebug = debug.extend('send');
    while (attempts <= this.maxAttempts) {
      try {
        const requestStartedAt = new Date().toISOString();
        methodDebug('Attempting promise', { attempts, requestStartedAt });

        const promiseResult = await promiseFunctionToRetry();
        return promiseResult;
      } catch (error) {
        if (
          error?.response?.status &&
          this.httpCodesToFailImmediately.includes(error.response.status)
        ) {
          throw error;
        }

        attempts++;
        const retryMilliseconds = getRetryMillisecondsWithExponentialBackoff(attempts);
        const approximateRetryAt = new Date(Date.now() + retryMilliseconds).toISOString();
        methodDebug('Promise failed', {
          error,
          retryMillisecondsWithExponentialBackoff: retryMilliseconds,
          approximateRetryAt,
        });

        await sleep(retryMilliseconds);
      }
    }

    const outOfRetriesMessage = `Unable to complete promise after ${this.maxAttempts} tries`;
    const error = new Error(outOfRetriesMessage);
    this.errorLoggingFn(outOfRetriesMessage, error, 'RetryPromiseWithBackoff');
    throw new Error(outOfRetriesMessage);
  }
}
